import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import trackViewedSpotDetailsReviewSummary from 'segment/events/viewed-spot-details-review-summary';
/**
 * Custom hook to track when the reviews section enters the viewport.
 * Fires the "Viewed Spot Details Review Summary" Segment event once per
 * facility, and resets tracking when `facilityId` changes.
 *
 * @param facilityId - The parking spot/facility ID
 * @param facilityName - The parking spot/facility name
 * @param starRating - The average star rating
 * @param count - The number of ratings
 * @returns ref - The ref to attach to the element to track viewport entry
 */
export const useReviewsViewportTracking = ({ facilityId, facilityName, starRating, count, }) => {
    const hasTrackedRef = useRef(false);
    const previousFacilityIdRef = useRef(facilityId);
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });
    // Reset tracking flag when facilityId changes
    useEffect(() => {
        if (previousFacilityIdRef.current !== facilityId) {
            hasTrackedRef.current = false;
            previousFacilityIdRef.current = facilityId;
        }
    }, [facilityId]);
    useEffect(() => {
        if (inView && !hasTrackedRef.current && facilityId && facilityName) {
            hasTrackedRef.current = true;
            trackViewedSpotDetailsReviewSummary({
                parkingSpotId: facilityId,
                parkingSpotName: facilityName,
                starRating,
                count,
            });
        }
    }, [inView, facilityId, facilityName, starRating, count]);
    return { ref };
};
